// ÍNDICE DE BUSCA DO TENANT — motor da busca inteligente de localização (hero 10 / topo 8).
//
// Por que existe: o campo de localização dos heros é um <select> de CIDADE. Quem procura por
// bairro ("Centro") ou por condomínio ("Reserva da Serra") não tem por onde — precisa entrar na
// busca avançada. Aqui montamos um índice único do tenant (bairros + cidades + empreendimentos +
// tipos) e filtramos NO CLIENTE, o que dá sugestão instantânea, sem ida ao servidor por tecla.
//
// Cabe no cliente porque o índice é pequeno: o tenant de referência (cs2606) tem 9 cidades,
// 184 bairros, 211 empreendimentos e 15 tipos — ~419 entradas. `LIMITE_INDICE` protege o caso
// de um tenant grande demais (aí o campo degrada para o <select> de sempre).
//
// Tudo aqui é PURO (string/array) de propósito: dá para testar sem DOM e sem rede.
import { removerAcentos } from './FunctionsGlobals';

export type CategoriaSugestao = 'bairro' | 'empreendimento' | 'cidade' | 'tipo' | 'codigo';

export interface Sugestao {
  categoria: CategoriaSugestao;
  /** id da entidade no CRM (0 para `codigo`, que não é entidade). */
  id: number;
  /** "Centro" — o que o usuário reconhece. */
  label: string;
  /** "Jundiaí" — desambiguador. Só bairro tem hoje (a cidade dele). */
  sublabel?: string;
  /** quantidade de imóveis ativos, quando o backend informa (bairro e cidade). */
  qtd?: number;
  /** cidade do bairro — obrigatória na URL, senão a listagem ignora o bairro. */
  cidadeLabel?: string;
  /** chave normalizada usada no matching (sem acento, minúscula). */
  busca: string;
  /**
   * Posição na lista de origem. Quando presente, é o desempate — a lista já chega ordenada
   * por quem a montou (ex.: `formatSelects` sobe as cidades priorizadas do tenant para o
   * topo) e reordenar aqui jogaria essa escolha fora. Ausente = desempate por volume/alfabético.
   */
  ordem?: number;
}

/** Acima disso o índice não é carregado e o campo degrada para o <select> de cidade. */
export const LIMITE_INDICE = 4000;

/** Rótulo de cada grupo na lista, e a ordem em que os grupos aparecem. */
export const GRUPOS: Array<{ categoria: CategoriaSugestao; titulo: string }> = [
  { categoria: 'codigo', titulo: 'Código' },
  { categoria: 'bairro', titulo: 'Bairros' },
  { categoria: 'empreendimento', titulo: 'Condomínios e empreendimentos' },
  { categoria: 'cidade', titulo: 'Cidades' },
  { categoria: 'tipo', titulo: 'Tipos de imóvel' },
];

/** Mesmo formato de utils/legacy-redirects.ts (REFERENCIA_NA_URL): CA36575, AP2380, TE16556. */
const REFERENCIA = /^[A-Za-z]{2,3}\d{2,6}$/;

export const normalizaTermo = (valor: unknown): string =>
  removerAcentos(String(valor ?? ''))
    .toLowerCase()
    .replace(/\s+/g, ' ')
    .trim();

/** O termo digitado parece um código de imóvel (AP2194) ou um número de referência? */
export const pareceCodigo = (termo: string): boolean => {
  const limpo = String(termo || '').trim();
  return REFERENCIA.test(limpo) || /^\d{3,8}$/.test(limpo);
};

const texto = (valor: unknown): string => String(valor ?? '').trim();
const inteiro = (valor: unknown): number => {
  const n = Number(valor);
  return Number.isFinite(n) && n > 0 ? Math.trunc(n) : 0;
};

export interface FontesIndice {
  /** resposta de services/selectors.getLocalidades → data.localidades */
  localidades?: any;
  /** `allTypes` que o hero já recebe: [{ label, value }] */
  tipos?: Array<{ label: string; value: any }>;
}

/**
 * Junta as fontes num vetor único de sugestões. Devolve `[]` quando não há dado — o
 * componente trata isso como "índice indisponível" e mantém o campo utilizável.
 */
export function montaIndice({ localidades, tipos }: FontesIndice): Sugestao[] {
  const cidadesBrutas: any[] = Array.isArray(localidades?.cidades) ? localidades.cidades : [];
  const bairrosBrutos: any[] = Array.isArray(localidades?.bairros) ? localidades.bairros : [];
  const empreendimentosBrutos: any[] = Array.isArray(localidades?.empreendimentos)
    ? localidades.empreendimentos
    : [];

  // id da cidade -> nome, para rotular o bairro como "Centro, Jundiaí".
  const nomeDaCidade = new Map<number, string>();
  for (const cidade of cidadesBrutas) {
    const id = inteiro(cidade?.id);
    const nome = texto(cidade?.nome);
    if (id && nome) nomeDaCidade.set(id, nome);
  }

  const indice: Sugestao[] = [];
  const push = (s: Omit<Sugestao, 'busca'>) => {
    if (!s.label) return;
    indice.push({ ...s, busca: normalizaTermo(`${s.label} ${s.sublabel || ''}`) });
  };

  for (const bairro of bairrosBrutos) {
    const cidadeLabel = nomeDaCidade.get(inteiro(bairro?.id_cidade)) || '';
    push({
      categoria: 'bairro',
      id: inteiro(bairro?.id),
      label: texto(bairro?.nome),
      sublabel: cidadeLabel || undefined,
      cidadeLabel: cidadeLabel || undefined,
      qtd: inteiro(bairro?.imoveis) || undefined,
    });
  }

  for (const empreendimento of empreendimentosBrutos) {
    push({
      categoria: 'empreendimento',
      id: inteiro(empreendimento?.id),
      label: texto(empreendimento?.nome),
    });
  }

  for (const cidade of cidadesBrutas) {
    push({
      categoria: 'cidade',
      id: inteiro(cidade?.id),
      label: texto(cidade?.nome),
      qtd: inteiro(cidade?.imoveis) || undefined,
    });
  }

  for (const tipo of tipos || []) {
    push({ categoria: 'tipo', id: inteiro(tipo?.value), label: texto(tipo?.label) });
  }

  return indice;
}

// Grau de aderência do termo à sugestão. Menor = melhor.
// 0 = o rótulo COMEÇA com o termo ("centro" -> "Centro")
// 1 = alguma PALAVRA começa com o termo ("serra" -> "Reserva da Serra" não; "da" sim)
// 2 = aparece em qualquer posição ("entro" -> "Centro")
const grau = (busca: string, termo: string): number => {
  if (busca.startsWith(termo)) return 0;
  if (busca.includes(` ${termo}`)) return 1;
  return busca.includes(termo) ? 2 : -1;
};

export interface GrupoResultado {
  categoria: CategoriaSugestao;
  titulo: string;
  itens: Sugestao[];
}

/**
 * Filtra o índice pelo termo e devolve os resultados JÁ AGRUPADOS por categoria, na ordem de
 * GRUPOS. Dentro do grupo: melhor aderência primeiro; empatou, mais imóveis primeiro; empatou de
 * novo, alfabético. É isso que põe "Centro, Jundiaí" (8 imóveis) acima de
 * "CENTRO (JACARÉ), Cabreúva" (1 imóvel).
 */
export function filtraIndice(
  indice: Sugestao[],
  termoBruto: string,
  limitePorGrupo = 6,
  // Mínimo de caracteres para o filtro valer. `0` lista o índice inteiro com o campo vazio —
  // é o que o hero 8 usa para a Cidade se comportar como um <select> (mostra tudo ao focar) e
  // virar filtro conforme a pessoa digita.
  minimoTermo = 2,
  // Sugerir "código do imóvel" só faz sentido no campo de localização. Num campo de escolha
  // fechada (tipo, dormitórios) digitar "123" não pode inventar um grupo Código.
  comCodigo = true
): GrupoResultado[] {
  const termo = normalizaTermo(termoBruto);
  if (termo.length < minimoTermo) return [];

  const porCategoria = new Map<CategoriaSugestao, Array<{ s: Sugestao; g: number }>>();

  for (const s of indice) {
    const g = grau(s.busca, termo);
    if (g < 0) continue;
    const lista = porCategoria.get(s.categoria) || [];
    lista.push({ s, g });
    porCategoria.set(s.categoria, lista);
  }

  const grupos: GrupoResultado[] = [];

  // O código não vem do índice: é derivado do próprio termo digitado.
  if (comCodigo && pareceCodigo(termoBruto)) {
    grupos.push({
      categoria: 'codigo',
      titulo: 'Código',
      itens: [
        {
          categoria: 'codigo',
          id: 0,
          label: String(termoBruto).trim().toUpperCase(),
          busca: termo,
        },
      ],
    });
  }

  for (const { categoria, titulo } of GRUPOS) {
    if (categoria === 'codigo') continue;
    const lista = porCategoria.get(categoria);
    if (!lista?.length) continue;

    lista.sort((a, b) => {
      if (a.g !== b.g) return a.g - b.g;
      // Lista que já vem ordenada de fora manda: preserva a ordem dela.
      if (a.s.ordem !== undefined && b.s.ordem !== undefined) return a.s.ordem - b.s.ordem;
      const qtdA = a.s.qtd || 0;
      const qtdB = b.s.qtd || 0;
      if (qtdA !== qtdB) return qtdB - qtdA;
      return a.s.label.localeCompare(b.s.label, 'pt-BR');
    });

    grupos.push({ categoria, titulo, itens: lista.slice(0, limitePorGrupo).map((x) => x.s) });
  }

  return grupos;
}

/** Quantos itens a lista tem no total (para o estado "nada encontrado"). */
export const totalDeItens = (grupos: GrupoResultado[]): number =>
  grupos.reduce((soma, grupo) => soma + grupo.itens.length, 0);

/** Texto que vai para o input depois de escolher — o mesmo que a pessoa leu na lista. */
export const rotuloCompleto = (s: Sugestao): string =>
  s.sublabel ? `${s.label}, ${s.sublabel}` : s.label;
